/**
 * Geocodificador especializado para puntos de encuentro ante maremotos en Andalucía
 *
 * Conecta con DERA G12 (Servicios) para acceder a la capa:
 * - `g12_38_PuntosEncuentroMaremotos` — 715 features Andalucía (verif. empírica 2026-05-18)
 *   distribuidas en las 5 provincias litorales (Málaga 89, Cádiz 85, Almería 57,
 *   Huelva 37, Granada 32 · sample n=300/715).
 *
 * Contexto institucional:
 * - Decreto 197/2024 (PTEL · municipios litorales)
 * - Plan de Emergencia Especial frente al Riesgo de Maremotos en Andalucía
 *
 * Fuente oficial:
 * - WFS DERA G12: https://www.ideandalucia.es/services/DERA_g12_servicios/wfs
 * - Layer:        DERA_g12_servicios:g12_38_PuntosEncuentroMaremotos
 *
 * Schema empírico (`DescribeFeatureType` 2026-05-18, persistido en
 * `.ptel/p0_wave1_maremotos_20260518/dft_g12_38.xml`):
 *
 * | Campo       | Tipo XSD       | Nillable | Cardinalidad |
 * |:------------|:---------------|:---------|:-------------|
 * | id_dera     | xsd:long       | NO       | 1..1         |
 * | localidad   | xsd:string     | YES      | 0..1         |
 * | cod_mun     | xsd:string     | YES      | 0..1 (INE5)  |
 * | municipio   | xsd:string     | YES      | 0..1         |
 * | provincia   | xsd:string     | YES      | 0..1         |
 * | geom        | MultiPoint     | YES      | 0..1 (card.1)|
 *
 * NOTA HX-CAPTURA-MAXIMAL (rumbo 2 · 1.ª aplicación productiva 2026-05-18):
 * - `parseFeature` preserva la totalidad del schema fuente en `properties`, no
 *   un denominador común. Esto garantiza que la BBDD enriquecida derivada del
 *   pipeline conserve los campos específicos de g12_38 (`localidad`,
 *   `id_dera`) aunque el flujo activo de geocoding no los necesite.
 * - Se añaden 4 campos de provenance (`source_layer`, `source_endpoint`,
 *   `schema_version`, `fetched_at`) que permiten re-validar o refrescar
 *   la BBDD sin re-consultar metadata externa.
 *
 * Tipología (TUN1 7-may, `18a3b737`): se reutiliza la macro existente
 * `TipologiaPTEL.EMERGENCIAS`. El detalle fino (`PUNTO_ENCUENTRO_MAREMOTO`) se
 * persiste como atributo libre, no como nueva categoría macro (TUN1: «No
 * Decreto fixes closed taxonomy»).
 *
 * @module services/geocoding/specialized
 * @since  2026-05-18 (WAVE-1 maremotos · rumbo 2)
 */

import type {
  WFSFeature,
  WFSSearchOptions,
} from './WFSBaseGeocoder';
import { WFSBaseGeocoder } from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import { getStringProp } from '../../../types/geojson';
import type { SpecializedGeocoderConfig } from '../../../types/infrastructure';

/**
 * Opciones de búsqueda específicas para puntos de encuentro maremoto.
 *
 * Extiende `WFSSearchOptions` con `locality` para filtrar por núcleo
 * poblacional concreto (~23 % de los features tienen `localidad`
 * NULL — verif. empírica n=300).
 */
export interface MaremotosSearchOptions extends WFSSearchOptions {
  /** Núcleo poblacional (`localidad`); NULL-permisivo en la fuente. */
  locality?: string;
}

/**
 * Identificador semántico granular del dataset (no es una `TipologiaPTEL`).
 *
 * Se persiste en `properties.tipoOriginal` para que la BBDD enriquecida
 * preserve la naturaleza específica del punto sin contaminar la taxonomía
 * macro cerrada de 19 categorías.
 */
export const TIPO_ORIGINAL_MAREMOTOS = 'PUNTO_ENCUENTRO_MAREMOTO' as const;

/**
 * Versión del schema empírico verificada. Incrementar si el
 * `DescribeFeatureType` upstream cambia (nuevos campos, nuevas
 * cardinalidades, cambio de tipo).
 */
export const SCHEMA_VERSION_G12_38 = 'g12_38_v20260518' as const;

/**
 * Geocodificador especializado para puntos de encuentro ante maremotos.
 *
 * Precisión esperada: ±5-10 m (coordenadas oficiales DERA G12 publicadas
 * en EPSG:25830 nativo, sin reproyección intermedia).
 *
 * Cobertura: 715 features distribuidos en las 5 provincias litorales
 * andaluzas (~88 % de los ~60 municipios litorales del Decreto 197/2024
 * cubiertos en sample n=300).
 *
 * Independiente de `WFSSecurityGeocoder` por decisión arquitectónica de
 * la sesión WAVE-1 (2026-05-18): los maremotos pueden vincularse a
 * seguridad en una situación operativa concreta, pero NO se mezclan los
 * datasets — schemas heterogéneos + dominios semánticos distintos.
 *
 * @example
 * ```typescript
 * const geocoder = new WFSMaremotosGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Punto de encuentro Elviria',
 *   municipality: 'Marbella',
 *   codMun: '29069',
 * });
 * // result.confidence >= 80 (match oficial DERA)
 * // result.properties.tipoOriginal === 'PUNTO_ENCUENTRO_MAREMOTO'
 * // result.properties.tipologiaPTEL === 'EMERGENCIAS'
 * ```
 */
export class WFSMaremotosGeocoder extends WFSBaseGeocoder {
  /** Endpoint unificado DERA G12 Servicios. */
  public static readonly DERA_G12_ENDPOINT =
    'https://www.ideandalucia.es/services/DERA_g12_servicios/wfs';

  /** Capa única consumida por este geocoder. */
  public static readonly LAYER_MAREMOTOS =
    'g12_38_PuntosEncuentroMaremotos';

  /** Identificador completo de la fuente para `properties.source_layer`. */
  public static readonly SOURCE_LAYER_ID =
    'DERA_g12_servicios:g12_38_PuntosEncuentroMaremotos';

  /**
   * Configuración por defecto.
   *
   * - `outputSRS = EPSG:25830` — fuente nativa, sin reproyección.
   * - `fuzzyThreshold = 0.45` — más laxo que Security (`0.35`) porque
   *   los puntos de encuentro carecen de campo `nombre` propio y el match
   *   se hace contra `localidad` + `municipio` (texto menos discriminante).
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: WFSMaremotosGeocoder.DERA_G12_ENDPOINT,
      layerName: WFSMaremotosGeocoder.LAYER_MAREMOTOS,
      fuzzyThreshold: 0.45,
      timeout: 15000,
      outputSRS: 'EPSG:25830',
    };
  }

  /**
   * Parsea un feature GeoJSON aplicando HX-CAPTURA-MAXIMAL.
   *
   * Mantiene:
   * 1. Atributos fuente íntegros (`...props`).
   * 2. Campos provenance (capa, endpoint, schema, timestamp).
   * 3. Campos semánticos PTEL (tipología macro + tipo original).
   *
   * `MultiPoint` cardinalidad=1 en 100 % del sample n=300 (verif.
   * empírica): `parseGeometry` de la base normaliza a `[x, y]` y se
   * registra `geom_type` + `geom_pointCount` para preservar fidelidad
   * al schema fuente.
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      const pt = this.parseGeometry(
        geom,
        `Maremotos feature ${String(feature.id ?? 'sin-id')}`,
      );
      if (!pt) {
        console.warn(
          'Feature maremotos sin geometría válida:',
          feature.id,
        );
        return null;
      }

      const [x, y] = pt;

      // Validación rango UTM30 Andalucía
      // (skill ptel-geocoding: X∈[100000,900000] Y∈[3900000,4400000])
      if (x < 100000 || x > 900000 || y < 3900000 || y > 4400000) {
        console.warn(
          'Maremotos: coordenadas fuera de rango Andalucía:',
          x,
          y,
        );
        return null;
      }

      // Cálculo cardinalidad MultiPoint (preserva fidelidad al schema
      // fuente aunque el sample n=300 reportó 100 % cardinalidad=1).
      const geomType = geom?.type ?? 'unknown';
      const geomPointCount =
        geom && geom.type === 'MultiPoint' && Array.isArray(geom.coordinates)
          ? geom.coordinates.length
          : 1;

      // Nombre canónico para el motor de fuzzy-matching:
      // localidad → municipio → fallback descriptivo.
      const localidad = getStringProp(props, 'localidad');
      const municipio = getStringProp(props, 'municipio');
      const displayName =
        localidad ||
        municipio ||
        `Punto encuentro maremoto ${String(props.id_dera ?? feature.id ?? '?')}`;

      return {
        name: displayName,
        x,
        y,
        municipality: municipio,
        province: getStringProp(props, 'provincia'),
        // `address` se mantiene vacío: la fuente no expone dirección postal.
        address: '',
        properties: {
          // HX-CAPTURA-MAXIMAL · campos fuente íntegros
          ...props,

          // Campos derivados (geometría)
          geom_type: geomType,
          geom_pointCount: geomPointCount,

          // Campos semánticos PTEL (2A · TipologiaPTEL macro cerrada)
          tipologiaPTEL: 'EMERGENCIAS',
          tipoOriginal: TIPO_ORIGINAL_MAREMOTOS,
          decretoReferencia: '197/2024',
          contextoEmergencia: 'EVACUACION_MAREMOTO_LITORAL',

          // Campos provenance (3B · HX-CAPTURA-MAXIMAL pleno)
          source_layer: WFSMaremotosGeocoder.SOURCE_LAYER_ID,
          source_endpoint: WFSMaremotosGeocoder.DERA_G12_ENDPOINT,
          schema_version: SCHEMA_VERSION_G12_38,
          fetched_at: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error('Error parseando feature maremotos:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL específico para `g12_38`.
   *
   * Override completo (no invoca `super`) — el filtro base usa claves
   * mayúsculas legacy que GeoServer DERA G12 rechaza con HTTP 400
   * (case-sensitive en CQL property names, verif. WSSD 15-may).
   *
   * Orden de preferencia:
   * 1. `cod_mun = 'X'` (INE5 exacto) si se aporta `codMun`.
   * 2. `municipio ILIKE '%X%'` (texto libre) si solo hay `municipality`.
   * 3. `provincia ILIKE '%X%'` (filtro provincial adicional).
   * 4. `localidad ILIKE '%X%'` (filtro fino opcional — 77 % de los
   *    features tienen valor; el filtro implícitamente excluye NULL).
   * 5. `BBOX(geom, …, 'EPSG:25830')` (filtro espacial).
   */
  protected buildCQLFilter(options: MaremotosSearchOptions): string {
    const filters: string[] = [];

    if (options.codMun) {
      filters.push(`cod_mun='${this.escapeCQL(options.codMun)}'`);
    } else if (options.municipality) {
      filters.push(
        `municipio ILIKE '%${this.escapeCQL(options.municipality)}%'`,
      );
    }

    if (options.province) {
      filters.push(
        `provincia ILIKE '%${this.escapeCQL(options.province)}%'`,
      );
    }

    if (options.locality) {
      filters.push(
        `localidad ILIKE '%${this.escapeCQL(options.locality)}%'`,
      );
    }

    if (options.bbox) {
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(
        `BBOX(geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`,
      );
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }
}
